/**
 * Connection Status Context
 *
 * Provides connection status monitoring using a passive, efficient approach:
 *
 * PASSIVE MONITORING (when connected):
 * - No background health checks or polling
 * - Connection failures are detected through normal user API requests
 * - API interceptors automatically update connection status on failures
 * - Zero network overhead when user is idle
 * - Scales efficiently with thousands of concurrent users
 *
 * ACTIVE MONITORING (when disconnected):
 * - Only activates when a real user action encounters a connection failure
 * - Pings every 10 seconds to detect when connection is restored
 * - Automatically stops pinging once connection is back
 * - User gets timely notification when connectivity returns
 *
 * This approach eliminates continuous health check traffic while ensuring
 * users are notified of connectivity issues when they actually matter.
 */
import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import axios from 'axios'; // Import axios for direct pings
import connectionStatusService from '../services/connectionStatusService';
import { config } from '../../config/runtime-config';

type Status = 'connected' | 'disconnected';
const RECONNECT_PING_INTERVAL = 10000; // Ping every 10 seconds when disconnected

interface ConnectionStatusContextType {
  status: Status;
}

const ConnectionStatusContext = createContext<ConnectionStatusContextType | undefined>(undefined);

export const useConnectionStatus = () => {
  const context = useContext(ConnectionStatusContext);
  if (!context) {
    throw new Error('useConnectionStatus must be used within a ConnectionStatusProvider');
  }
  return context;
};

export const ConnectionStatusProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<Status>(connectionStatusService.getStatus());
  const pingIntervalId = useRef<NodeJS.Timeout | null>(null);

  const stopReconnectPinging = () => {
    if (pingIntervalId.current) {
      clearInterval(pingIntervalId.current);
      pingIntervalId.current = null;
      console.log('Connection Status: Stopped reconnection pinging.');
    }
  };

  const startReconnectPinging = () => {
    stopReconnectPinging(); // Clear any existing interval first
    console.log('Connection Status: Disconnected during active use. Starting reconnection pinging...');

    pingIntervalId.current = setInterval(async () => {
      console.log('Connection Status: Pinging server for reconnection...');
      try {
        // Use axios directly to bypass ApiService interceptors for the ping
        // We just want to know if the server is reachable at the network level
        // A response (even an error like 4xx/5xx) means connectivity is back.
        await axios.get('/health', {
          baseURL: config.apiUrl.replace('/api/v2', ''), // Use runtime config base URL
          timeout: 5000 // Short timeout for pings
        });
        // If the request didn't throw (meaning we got *any* response)
        console.log('Connection Status: Ping successful, connection restored.');
        connectionStatusService.setStatus('connected');
      } catch (error: any) {
        // Check if it's a network error (no response received)
        if (!error.response) {
          console.log('Connection Status: Ping failed (network error), still disconnected.');
          // Keep status as disconnected, interval continues
        } else {
          // Received an error response (e.g., 4xx, 5xx) - server is reachable
          console.log(`Connection Status: Ping received error response (${error.response.status}), but connection is considered restored.`);
          connectionStatusService.setStatus('connected');
        }
      }
    }, RECONNECT_PING_INTERVAL);
  };

  useEffect(() => {
    const handleStatusChange = (newStatus: Status) => {
      console.log(`Connection Status Context: Received status update - ${newStatus}`);
      setStatus(newStatus);
      if (newStatus === 'disconnected') {
        // Start pinging to detect when connection is restored
        // This only happens when the user is actively using the app and we detect a failure
        startReconnectPinging();
      } else {
        // Connection restored - stop pinging
        stopReconnectPinging();
        console.log('Connection Status: Connected. Using passive monitoring via API requests.');
      }
    };

    // Subscribe to status changes
    connectionStatusService.on(handleStatusChange);

    // Initial setup based on current status
    const currentStatus = connectionStatusService.getStatus();
    if (currentStatus === 'disconnected') {
      // If we start in disconnected state, begin pinging
      startReconnectPinging();
    } else {
      // If we start connected, no active monitoring needed
      // Actual API requests will detect connection issues
      console.log('Connection Status: Starting in connected state. Using passive monitoring via API requests.');
    }

    // Unsubscribe and clear intervals on cleanup
    return () => {
      console.log('Connection Status Context: Cleaning up...');
      connectionStatusService.off(handleStatusChange);
      stopReconnectPinging();
    };
  }, []); // Empty dependency array ensures this runs only once on mount

  return (
    <ConnectionStatusContext.Provider value={{ status }}>
      {children}
    </ConnectionStatusContext.Provider>
  );
};
